import type { HumanName, Practitioner } from '@medplum/fhirtypes'
import { formatHumanName } from '@medplum/core'
import {
  PRACTITIONER_QUALIFICATION_SYSTEM,
  QUALIFICATION_LICENSE_SPECIALTY_EXTENSION_URL,
} from '@/model/middleware-types'

/**
 * Formats a practitioner's HumanName as "Given Family, Suffix" (e.g. "Doctor Clark, FNP").
 * Falls back to Medplum's formatHumanName when no suffix is present.
 */
export function formatProviderName(name: HumanName | undefined): string {
  if (!name) return ''
  const given = (name.given ?? []).join(' ')
  const family = name.family ?? ''
  const base = [given, family].filter(Boolean).join(' ')
  const suffixes = (name.suffix ?? []).join(', ')
  if (base && suffixes) return `${base}, ${suffixes}`
  return formatHumanName(name)
}

export function getPractitionerName(practitioner: Practitioner | undefined): string {
  if (!practitioner) {
    return 'Unknown practitioner'
  }

  return formatHumanName(practitioner.name?.[0]) || practitioner.id || 'Unknown practitioner'
}

/**
 * The credential suffix rendered after a name — `Alexander Shoykhet, MD`.
 *
 * Mirrors `primaryCredentialSuffix` in
 * packages/middleware/src/practitioner/qualification.util.ts: the first
 * qualification wins, and its specialty is preferred over the bare degree.
 */
function getCredentialSuffix(practitioner: Practitioner | undefined): string | undefined {
  for (const qualification of practitioner?.qualification ?? []) {
    const degree = qualification.code?.coding?.find(
      (coding) => coding.system === PRACTITIONER_QUALIFICATION_SYSTEM,
    )?.code
    if (!degree) {
      continue
    }

    const specialty = qualification.extension?.find(
      (extension) => extension.url === QUALIFICATION_LICENSE_SPECIALTY_EXTENSION_URL,
    )?.valueCode

    return specialty ?? degree
  }

  return undefined
}

/**
 * `Alexander Shoykhet, MD` — or, with no qualification, the name with any
 * `name.suffix` after a comma (`Jane Doe, FNP`), else just the name.
 */
export function getPractitionerDisplayName(practitioner: Practitioner | undefined): string {
  const credential = getCredentialSuffix(practitioner)
  if (!practitioner || !credential) {
    return formatProviderName(practitioner?.name?.[0]) || getPractitionerName(practitioner)
  }

  // The credential is stored twice: middleware writes it into `name.suffix` as
  // well as `qualification` (invite-org-member.handler.ts), and
  // `formatHumanName` renders suffixes by default. Formatting without the
  // suffix keeps `qualification` the single source for the credential shown
  // here, instead of rendering it twice as `Doctor X MD, MD`.
  const name =
    formatHumanName(practitioner.name?.[0], { suffix: false }) ||
    practitioner.id ||
    'Unknown practitioner'

  return `${name}, ${credential}`
}
